import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmDeleteModal } from '../../components/common/ConfirmDeleteModal';
import { DataTable, type Column } from '../../components/table/DataTable';
import {
  deleteRole,
  listActiveRoles,
  listApplications,
  searchRoles,
  type ApplicationOption,
  type RoleListItem,
  type RoleStatusFilter,
} from '../../services/roleAdministration';
import { consumeFlashToast } from '../../utils/flashToast';
import { alSelClass } from '../../utils/alSelClass';

export default function RolesPermissionsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<RoleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const location = useLocation();

  const [roleName, setRoleName] = useState<string>('');
  const [apps, setApps] = useState<ApplicationOption[]>([]);
  const [applicationId, setApplicationId] = useState<number | ''>('');
  const [status, setStatus] = useState<RoleStatusFilter>('');
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<RoleListItem | null>(null);

  function showMessage(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3200);
  }

  const runSearch = async (input?: { roleName?: string; applicationId?: number | ''; status?: RoleStatusFilter }) => {
    setLoading(true);
    const resolvedAppId = (() => {
      const v = input?.applicationId ?? applicationId;
      if (typeof v === 'number') return v;
      return undefined;
    })();
    const res = await searchRoles({
      roleName: input?.roleName ?? roleName,
      applicationId: resolvedAppId,
      status: input?.status ?? status,
    });
    if (res.success) setRows(res.data);
    setLoading(false);
  };

  const canDeleteRole = (r: RoleListItem) => !r.active;

  const runDelete = async (r: RoleListItem) => {
    setDeletingId(r.roleId);
    try {
      const res = await deleteRole(r.roleId);
      if (!res.success) throw new Error(res.message);
      setConfirmDelete(null);
      showMessage(t('admin.rolesPermissions.roleDeletedSuccess'));
      await runSearch();
    } catch (e: unknown) {
      showMessage(e instanceof Error ? e.message : t('admin.rolesPermissions.roleDeleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    let alive = true;

    const navToast = (location.state as { toast?: string } | null | undefined)?.toast;
    if (navToast) showMessage(navToast);

    const qsToast = (() => {
      const v = new URLSearchParams(location.search).get('toast');
      if (v === 'created') return t('admin.rolesPermissions.roleCreatedSuccess');
      if (v === 'updated') return t('admin.rolesPermissions.roleUpdatedSuccess');
      return null;
    })();
    if (qsToast) showMessage(qsToast);

    const flash = consumeFlashToast();
    if (flash?.message) showMessage(flash.message);

    void (async () => {
      let nextAppId = applicationId;
      const appRes = await listApplications();
      if (!alive) return;
      if (appRes.success && appRes.data.length) {
        setApps(appRes.data);
        // Keep placeholder until user selects. If current value is valid, preserve it.
        nextAppId = appRes.data.some((a) => a.id === applicationId) ? applicationId : '';
        setApplicationId(nextAppId);
      } else {
        setApps([]);
        nextAppId = '';
        setApplicationId('');
      }

      const roleRes = await listActiveRoles();
      if (!alive) return;
      if (roleRes.success && roleRes.data.length) {
        setRoleNames(roleRes.data.map((r) => r.roleName));
      } else {
        // fallback to whatever is present in the table data if API fails
        setRoleNames([]);
      }

      await runSearch({ applicationId: nextAppId });
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setApplicationId((cur) => {
      if (apps.length === 0) return '';
      if (typeof cur === 'number' && apps.some((a) => a.id === cur)) return cur;
      return '';
    });
  }, [apps]);

  const roleNameOptions = useMemo(() => {
    if (roleNames.length) return roleNames;
    const names = Array.from(new Set(rows.map((r) => r.roleName).filter(Boolean)));
    names.sort((a, b) => a.localeCompare(b));
    return names;
  }, [rows, roleNames]);

  const columns = useMemo<Column<RoleListItem>[]>(() => {
    return [
      { key: 'name', header: t('admin.shared.roleName'), getValue: (r) => r.roleName, render: (r) => <span style={{ fontWeight: 800 }}>{r.roleName}</span> },
      { key: 'code', header: t('admin.shared.code'), getValue: (r) => r.roleCode ?? r.metaCode ?? '—' },
      {
        key: 'app',
        header: t('admin.shared.applicationName'),
        getValue: (r) => r.applicationName ?? apps.find((a) => a.id === r.applicationId)?.label ?? '—',
        render: (r) => {
          const label = r.applicationName ?? apps.find((a) => a.id === r.applicationId)?.label;
          if (!label) return '—';
          return <span className="pill p-app">{label}</span>;
        },
      },
      { key: 'desc', header: t('admin.shared.description'), getValue: (r) => r.description, render: (r) => <span style={{ color: 'var(--t2)' }}>{r.description}</span> },
      { key: 'assigned', header: t('admin.shared.assignedUsers'), getValue: (r) => String(r.assignedUserCount ?? 0), searchable: false, sortable: false },
      {
        key: 'status',
        header: t('common.status'),
        getValue: (r) => (r.active ? 'Active' : 'Inactive'),
        render: (r) => (
          <span className={`pill ${r.active ? 'p-com' : 'p-arc'}`}>{r.active ? t('common.active') : t('common.inactive')}</span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            {r.isMeta ? (
              <Link
                className="act-b"
                to={`/admin/roles-permissions/${r.roleId}/view`}
                data-tip={t('admin.rolesPermissions.viewRoleDetails')}
                aria-label={t('admin.rolesPermissions.viewRoleDetails')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </Link>
            ) : (
              <>
                <Link
                  className="act-b"
                  to={`/admin/roles-permissions/${r.roleId}/view`}
                  data-tip={t('admin.rolesPermissions.viewRoleDetails')}
                  aria-label={t('admin.rolesPermissions.viewRoleDetails')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </Link>
                {r.allowUpdate === false ? (
                  <span
                    className="act-b"
                    aria-label={t('admin.rolesPermissions.updateNotAllowedAria')}
                    title={t('admin.rolesPermissions.updateNotAllowed')}
                    style={{ opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </span>
                ) : (
                  <Link
                    className="act-b"
                    to={`/admin/roles-permissions/${r.roleId}/edit`}
                    data-tip={t('admin.rolesPermissions.updateRoleDetails')}
                    aria-label={t('admin.rolesPermissions.updateRoleDetails')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </Link>
                )}
              </>
            )}
            {canDeleteRole(r) ? (
              <button
                className="act-b"
                type="button"
                disabled={deletingId === r.roleId}
                data-tip={t('admin.rolesPermissions.deleteRole')}
                aria-label={t('admin.rolesPermissions.deleteRole')}
                onClick={() => setConfirmDelete(r)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M3 6h18" />
                  <path d="M8 6V4h8v2" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
              </button>
            ) : null}
          </div>
        ),
      },
    ];
  }, [apps, deletingId, t]);

  return (
    <>
      <div className="bc">
        <span>{t('admin.userAdministration')}</span>
        <span className="bc-sep">/</span>
        {t('admin.rolesPermissions.title')}
      </div>

      <div className="page-hdr" style={{ marginBottom: 12 }}>
        <div className="pg-title" style={{ marginBottom: 0 }}>
          {t('admin.rolesPermissions.title')}
        </div>
        <Link to="/admin/roles-permissions/new" className="btn-create" style={{ textDecoration: 'none' }}>
          {t('admin.rolesPermissions.addNewRole')}
        </Link>
      </div>

      {toast ? (
        <div
          role="status"
          style={{
            marginBottom: 14,
            padding: '10px 14px',
            borderRadius: 'var(--rs)',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            fontSize: 13,
            color: 'var(--text)',
          }}
        >
          {toast}
        </div>
      ) : null}

      <div className="al-filter" style={{ marginBottom: 14 }}>
        <div className="al-filter-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
          <div className="al-f">
            <div className="al-l">{t('admin.shared.roleName')}</div>
            <select className={alSelClass(roleName)} value={roleName} onChange={(e) => setRoleName(e.target.value)}>
              <option value="" disabled hidden>
                {t('admin.rolesPermissions.selectRoleName')}
              </option>
              {roleNameOptions.map((nm) => (
                <option key={nm} value={nm}>
                  {nm}
                </option>
              ))}
            </select>
          </div>
          <div className="al-f">
            <div className="al-l">{t('admin.shared.applicationName')}</div>
            <select
              className={alSelClass(applicationId === '' ? '' : String(applicationId))}
              value={applicationId === '' ? '' : String(applicationId)}
              disabled={apps.length === 0}
              onChange={(e) => setApplicationId(e.target.value ? Number(e.target.value) : '')}
            >
              {apps.length === 0 ? (
                <option value="" disabled>
                  {t('common.noDataFound')}
                </option>
              ) : (
                <>
                  <option value="" disabled hidden>
                    {t('admin.rolesPermissions.selectApplicationName')}
                  </option>
                  {apps.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="al-f">
            <div className="al-l">{t('common.status')}</div>
            <select className={alSelClass(status)} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="" disabled hidden>
                {t('admin.shared.selectStatus')}
              </option>
              <option value="Active">{t('common.active')}</option>
              <option value="Inactive">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
          <button
            type="button"
            className="mfb"
            onClick={() => {
              setRoleName('');
              setApplicationId('');
              setStatus('');
              void runSearch({ roleName: '', applicationId: '', status: '' });
            }}
          >
            {t('common.reset')}
          </button>
          <button type="button" className="btn-create" onClick={() => void runSearch()}>
            {t('common.search')}
          </button>
        </div>
      </div>

      <DataTable
        title={t('admin.rolesPermissions.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        enableSorting={false}
        emphasizedHeader
        columns={columns}
        rows={rows}
        rowKey={(r) => String(r.roleId)}
        defaultRowsPerPage={5}
        rowsPerPageOptions={[5, 10, 15]}
      />

      <ConfirmDeleteModal
        open={!!confirmDelete}
        title={t('admin.rolesPermissions.deleteTitle')}
        entityLabel={t('admin.rolesPermissions.entityLabel')}
        entityName={confirmDelete?.roleName ?? ''}
        deleting={deletingId != null}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) void runDelete(confirmDelete);
        }}
      />
    </>
  );
}

